import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogArticles } from "@/mocks/blog";

function updateMetaTag(selector: string, attr: string, value: string) {
  let el = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("meta");
    if (selector.startsWith("meta")) {
      const name = selector.match(/meta\[name="([^"]*)"\]/)?.[1] || selector.match(/meta\[property="([^"]*)"\]/)?.[1] || "";
      el.setAttribute(name ? "property" : "name", name || "");
    }
    document.head.appendChild(el);
  }
  if (attr === "content") (el as HTMLMetaElement).content = value;
  if (attr === "href") (el as HTMLLinkElement).href = value;
}

function removeMetaTag(selector: string) {
  const el = document.querySelector(selector);
  if (el) el.remove();
}

// Remove the original title/og we injected so they don't stick across page navigations
const INJECTED_SELECTOR = "meta[name=\"injected-seo\"]";

const articleBodies: Record<string, { intro: string; sections: { heading: string; content: string }[]; faqs: { q: string; a: string }[] }> = {
  "sugargoo-spreadsheet-guide": {
    intro: "If you have spent any time in the Sugargoo shopping community, you have probably heard the term Sugargoo Spreadsheet thrown around quite often. But what exactly is it, and why has it become the go-to resource for thousands of smart shoppers across the United States, Europe, and beyond? This comprehensive guide breaks down everything you need to know.",
    sections: [
      { heading: "How Sugargoo Spreadsheet Works", content: "The concept behind every Sugargoo Spreadsheet is elegantly simple. Instead of browsing random product listings and hoping for the best, you open a shared spreadsheet that has already been organized by category, ranked by quality, and annotated with sizing advice and buyer reviews. Each row represents one product with its name, price, quality rating, size availability, and a direct purchase link." },
      { heading: "Why Shoppers Love Spreadsheet Systems", content: "Traditional shopping on agent platforms means manually searching through thousands of unverified listings. A curated Sugargoo Spreadsheet eliminates this friction entirely. Products are pre-screened for quality, organized into intuitive categories like Shoes, Hoodies, T-Shirts, Jackets, and Accessories, and come with community-verified ratings so you can trust what you are buying." },
      { heading: "Getting Started with Your First Spreadsheet", content: "Start by identifying which categories matter most to you. Shoe enthusiasts should look for spreadsheets with strong sneaker sections featuring detailed quality tiers. Streetwear fans will benefit from spreadsheets that prioritize hoodies and graphic tees. Once you find a well-maintained spreadsheet with recent update dates, bookmark it and check back regularly for fresh drops." },
    ],
    faqs: [
      { q: "Are Sugargoo Spreadsheets free to use?", a: "Yes, the vast majority are completely free community resources shared by experienced curators to help fellow shoppers discover quality products." },
      { q: "How often are spreadsheets updated?", a: "The best Sugargoo Spreadsheets are updated weekly or even daily, adding new product finds, removing dead links, and adjusting ratings based on community feedback." },
      { q: "Can I trust the quality ratings?", a: "Quality ratings in reputable spreadsheets are based on real community feedback and in-hand product reviews. Look for spreadsheets maintained by well-known community members with transparent review processes." },
      { q: "What categories are typically covered?", a: "Most comprehensive spreadsheets cover Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and more." },
      { q: "How do I find the best spreadsheet for me?", a: "Check our homepage categories to explore curated product lists across every major category, all linked to verified product pages on our main platform." },
    ],
  },
  "best-sugargoo-spreadsheet-shoes": {
    intro: "Shoes are the number one category shoppers look for in any Sugargoo Spreadsheet. From limited-edition sneakers to classic designer silhouettes, a well-curated shoe section can save you dozens of hours of research. This guide breaks down the best Sugargoo Spreadsheets specifically optimized for shoe shopping in 2026.",
    sections: [
      { heading: "What Makes a Great Shoe Spreadsheet", content: "The best shoe-focused Sugargoo Spreadsheets include detailed sizing charts, multiple quality tier breakdowns, and clear photos comparing retail versus product versions. Look for spreadsheets that separate sneakers by brand — Nike, Adidas, New Balance, and designer categories — so you can quickly navigate to what you want." },
      { heading: "Top Shoe Categories to Explore", content: "Sneakers dominate most Sugargoo Spreadsheet shoe sections, but you will also find dress shoes, boots, sandals, and athletic performance footwear. Pay attention to the batch dates listed next to each product — fresher batches typically have better quality and more accurate sizing." },
      { heading: "Reading Shoe Quality Ratings", content: "Shoe ratings in Sugargoo Spreadsheets typically follow a 1-10 scale. Anything rated 8+ is considered near-identical to retail. Ratings of 6-7 indicate solid quality with minor differences, while 5 and below are budget options where you should manage expectations on materials and comfort." },
    ],
    faqs: [
      { q: "Which shoe brands are most common in spreadsheets?", a: "Nike, Adidas, Jordan, New Balance, and luxury designer brands dominate shoe spreadsheets. You will find everything from trendy dunks to classic leather loafers." },
      { q: "How accurate are shoe sizes in spreadsheets?", a: "Most quality spreadsheets include sizing notes for each product. Always check the notes column — some shoes run small or large compared to retail sizing." },
      { q: "Are sneaker quality ratings reliable?", a: "Ratings from established spreadsheet curators are generally reliable. Cross-reference ratings with community reviews when possible for high-ticket purchases." },
    ],
  },
  "default": {
    intro: "This article is part of our Sugargoo Spreadsheet knowledge base — a growing collection of guides, comparisons, and tips designed to help you shop smarter and save money on every order.",
    sections: [
      { heading: "Understanding This Topic", content: "The Sugargoo Spreadsheet ecosystem covers a wide range of categories and shopping strategies. Whether you are a complete beginner or an experienced shopper, understanding how to leverage curated product lists can dramatically improve your shopping results." },
      { heading: "Key Takeaways", content: "Always verify the spreadsheet's last update date before relying on its links. Cross-reference quality ratings across multiple sources when possible. Bookmark your favorite spreadsheets and check back weekly for the best experience." },
      { heading: "Next Steps", content: "Explore our full collection of Sugargoo Spreadsheet guides covering everything from category-specific recommendations to safety tips and platform comparisons. Every guide is updated regularly to reflect the latest community insights and product availability." },
    ],
    faqs: [
      { q: "Where can I find more information?", a: "Browse our complete collection of Sugargoo Spreadsheet guides on the homepage. Each article covers a specific topic in detail with practical tips and comparison tables." },
      { q: "Are these guides updated regularly?", a: "Yes, we update all articles regularly to reflect the latest spreadsheet releases, community feedback, and shopping trends." },
    ],
  },
};

function getArticleContent(slug: string) {
  return articleBodies[slug] || articleBodies["default"];
}

export default function BlogPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = blogArticles.find((a) => a.slug === slug);
  const body = getArticleContent(slug || "");

  const schemaData = useMemo(() => {
    if (!article || !slug) return null;
    const baseUrl = import.meta.env.VITE_SITE_URL || "https://sugargoospreadsheet.asia";
    const articleUrl = `${baseUrl}/blog/${slug}`;
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": body.faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": f.a,
        },
      })),
    };
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: baseUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: `${baseUrl}/blog/sugargoo-spreadsheet-guide`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: articleUrl,
        },
      ],
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt,
      "image": article.image,
      "url": articleUrl,
      "author": {
        "@type": "Organization",
        "name": "SugarGoo Spreadsheet",
      },
      "publisher": {
        "@type": "Organization",
        "name": "SugarGoo Spreadsheet",
        "url": baseUrl,
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl,
      },
    };
    return { faqSchema, breadcrumbSchema, articleSchema };
  }, [article, body, slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - SugarGoo Spreadsheet`;
    } else {
      document.title = "Article Not Found - SugarGoo Spreadsheet";
    }
  }, [article]);

  // Dynamic meta tags for SEO
  useEffect(() => {
    if (!article) return;
    const siteUrl = import.meta.env.VITE_SITE_URL || "https://sugargoospreadsheet.asia";
    const articleUrl = `${siteUrl}/blog/${article.slug}`;
    const description = article.excerpt;

    updateMetaTag('meta[name="description"]', "content", description);
    updateMetaTag('meta[property="og:title"]', "content", `${article.title} - SugarGoo Spreadsheet`);
    updateMetaTag('meta[property="og:description"]', "content", description);
    updateMetaTag('meta[property="og:url"]', "content", articleUrl);
    updateMetaTag('meta[property="og:type"]', "content", "article");
    updateMetaTag('meta[property="og:image"]', "content", article.image);
    updateMetaTag('meta[name="twitter:title"]', "content", `${article.title} - SugarGoo Spreadsheet`);
    updateMetaTag('meta[name="twitter:description"]', "content", description);
    updateMetaTag('meta[name="twitter:card"]', "content", "summary_large_image");
    updateMetaTag('meta[name="twitter:image"]', "content", article.image);

    // Canonical URL
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalEl) {
      canonicalEl.href = articleUrl;
    } else {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      canonicalEl.href = articleUrl;
      document.head.appendChild(canonicalEl);
    }

    return () => {
      // Reset to homepage defaults on unmount
      document.title = "Sugargoo Spreadsheet Guide - Find the Best Spreadsheet for Sugargoo Shopping";
      updateMetaTag('meta[name="description"]', "content", "Discover the ultimate Sugargoo Spreadsheet guide for 2026. Compare curated spreadsheets for shoes, hoodies, t-shirts, jackets, and accessories. Find the best Sugargoo shopping spreadsheet to save time and money on your agent orders.");
      updateMetaTag('meta[property="og:title"]', "content", "Sugargoo Spreadsheet Guide - Find the Best Spreadsheet for Sugargoo Shopping");
      updateMetaTag('meta[property="og:description"]', "content", "Discover the ultimate Sugargoo Spreadsheet guide for 2026. Compare curated spreadsheets for shoes, hoodies, t-shirts, jackets, and accessories.");
      updateMetaTag('meta[property="og:url"]', "content", siteUrl);
      updateMetaTag('meta[property="og:type"]', "content", "website");
      updateMetaTag('meta[name="twitter:title"]', "content", "Sugargoo Spreadsheet Guide - Find the Best Spreadsheet for Sugargoo Shopping");
      updateMetaTag('meta[name="twitter:description"]', "content", "Discover the ultimate Sugargoo Spreadsheet guide for 2026. Compare curated spreadsheets, save time and money on your agent orders.");
      updateMetaTag('meta[name="twitter:card"]', "content", "summary_large_image");
      const canonicalReset = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (canonicalReset) canonicalReset.href = siteUrl;
    };
  }, [article]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center px-4">
            <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-4">Article Not Found</h1>
            <p className="mt-2 text-sm text-foreground-500">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="mt-6 inline-block px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors">
              Back to Homepage
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="bg-background-50">
      {/* Schema.org structured data */}
      {schemaData && (
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData.breadcrumbSchema) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData.articleSchema) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData.faqSchema) }}
          />
        </>
      )}

      <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        </div>
        <Navbar />
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 pt-16">
          <div className="flex items-center gap-2 text-background-50/70 text-xs mb-4 flex-wrap">
            <Link to="/" className="hover:text-background-50 transition-colors">Home</Link>
            <span>/</span>
            <Link to="/blog/sugargoo-spreadsheet-guide" className="hover:text-background-50 transition-colors">Blog</Link>
            <span>/</span>
            <span className="text-background-50/90">{article.category}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 max-w-3xl leading-tight">
            {article.title}
          </h1>
        </div>
      </section>

      <article
        itemScope
        itemType="https://schema.org/Article"
        className="max-w-[780px] mx-auto px-4 md:px-6 py-12 md:py-16"
      >
        <meta itemProp="headline" content={article.title} />
        <meta itemProp="description" content={article.excerpt} />
        <meta itemProp="image" content={article.image} />

        <div className="prose-custom">
          <p className="text-lg text-foreground-700 leading-relaxed">
            {body.intro}
          </p>

          <p className="mt-5 text-foreground-700 leading-relaxed">
            A <strong>Sugargoo Spreadsheet</strong> is more than just a list of links — it is a carefully maintained resource built by experienced community members who dedicate their time to testing products, verifying seller reliability, and organizing finds into an accessible format. Whether you are hunting for the latest sneaker releases or searching for premium hoodies at fair prices, the right spreadsheet puts everything you need at your fingertips.
          </p>

          {body.sections.map((section, idx) => (
            <div key={idx}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-5">
                {section.heading}
              </h2>
              <p className="text-foreground-700 leading-relaxed">
                {section.content}
              </p>
            </div>
          ))}

          <div className="mt-10 overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-4 py-3 font-semibold text-foreground-950">Feature</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-950">Sugargoo Spreadsheet</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-950">Direct Browsing</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-background-200/50">
                  <td className="px-4 py-3 font-medium text-foreground-800">Discovery Speed</td>
                  <td className="px-4 py-3 text-foreground-600">Minutes with curated lists</td>
                  <td className="px-4 py-3 text-foreground-600">Hours of manual searching</td>
                </tr>
                <tr className="border-t border-background-200/50 bg-background-50/50">
                  <td className="px-4 py-3 font-medium text-foreground-800">Quality Verification</td>
                  <td className="px-4 py-3 text-foreground-600">Community-rated and reviewed</td>
                  <td className="px-4 py-3 text-foreground-600">Unverified, buyer beware</td>
                </tr>
                <tr className="border-t border-background-200/50">
                  <td className="px-4 py-3 font-medium text-foreground-800">Price Comparison</td>
                  <td className="px-4 py-3 text-foreground-600">Built-in comparison tables</td>
                  <td className="px-4 py-3 text-foreground-600">Manual tab switching</td>
                </tr>
                <tr className="border-t border-background-200/50 bg-background-50/50">
                  <td className="px-4 py-3 font-medium text-foreground-800">Update Frequency</td>
                  <td className="px-4 py-3 text-foreground-600">Weekly or daily refreshes</td>
                  <td className="px-4 py-3 text-foreground-600">Static, often outdated</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-5">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {body.faqs.map((faq, idx) => (
              <details key={idx} className="group border border-background-200/70 rounded-xl overflow-hidden">
                <summary className="px-5 py-4 font-medium text-foreground-900 cursor-pointer hover:bg-background-100 transition-colors flex items-center justify-between">
                  {faq.q}
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>

          <div className="mt-12 p-8 rounded-2xl bg-foreground-950 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50">
              Ready to Start Shopping Smarter?
            </h3>
            <p className="mt-2 text-sm text-foreground-400 max-w-md mx-auto">
              Explore our full collection of curated Sugargoo Spreadsheet products across every category.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Explore Full Spreadsheet
              </a>
              <Link
                to="/"
                className="whitespace-nowrap px-6 py-3 border border-foreground-700 text-foreground-300 rounded-full text-sm font-medium hover:border-foreground-500 hover:text-background-50 transition-colors"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          <div className="mt-12 pt-10 border-t border-background-200/70">
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">
              Related Articles
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {blogArticles.filter(a => a.slug !== slug).slice(0, 3).map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  className="rounded-xl border border-background-200/70 p-4 hover:border-primary-200 transition-colors group"
                >
                  <div className="h-24 rounded-lg overflow-hidden mb-3">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {related.title}
                  </p>
                  <span className="text-xs text-foreground-400 mt-1 block">{related.readTime}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}